// frontend/src/pages/FleetTeamPage.js
import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import useApi from '../hooks/useApi';
import { 
    Plus, Search, Edit, Trash2, X, Users, ChevronDown, ChevronUp, User, CreditCard, ChevronLeft, ChevronRight, List
} from 'lucide-react';

// --- NOVO COMPONENTE: CAMPO DE BUSCA INTELIGENTE DE USUÁRIOS ---
const UserSearchInput = ({ value, onChange, onSelect, usersList }) => {
    const [showDropdown, setShowDropdown] = useState(false);
    const wrapperRef = useRef(null);

    // Fecha o dropdown ao clicar fora
    useEffect(() => {
        function handleClickOutside(event) {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
                setShowDropdown(false);
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [wrapperRef]);

    // Filtra os usuários baseado no que foi digitado (Nome, Username, CPF ou CNH)
    const filteredUsers = useMemo(() => {
        if (!value) return [];
        const lowerVal = value.toLowerCase();
        return usersList.filter(u => 
            (u.full_name && u.full_name.toLowerCase().includes(lowerVal)) ||
            (u.username && u.username.toLowerCase().includes(lowerVal)) ||
            (u.cpf && u.cpf.replace(/\D/g, '').includes(lowerVal.replace(/\D/g, ''))) ||
            (u.cnh && u.cnh.toLowerCase().includes(lowerVal))
        ).slice(0, 5); // Mostra no máximo 5 resultados para não poluir a tela
    }, [value, usersList]);

    return (
        <div className="relative w-full" ref={wrapperRef}>
            <input
                type="text"
                placeholder="Nome completo..."
                value={value}
                onChange={(e) => {
                    onChange(e.target.value);
                    setShowDropdown(true);
                }}
                onFocus={() => setShowDropdown(true)}
                className="w-full p-2 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 outline-none"
            />
            {showDropdown && filteredUsers.length > 0 && (
                <ul className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-48 overflow-y-auto">
                    {filteredUsers.map(u => (
                        <li 
                            key={u.id}
                            onClick={() => {
                                onSelect(u);
                                setShowDropdown(false);
                            }}
                            className="p-2 hover:bg-blue-50 cursor-pointer border-b border-gray-100 last:border-0 transition-colors"
                        >
                            <p className="font-bold text-sm text-gray-800 truncate">{u.full_name || u.username}</p>
                            <p className="text-[10px] text-gray-500 font-medium mt-0.5">
                                {u.cpf ? `CPF: ${u.cpf}` : ''} {u.cpf && u.cnh ? ' | ' : ''} {u.cnh ? `CNH: ${u.cnh}` : ''}
                            </p>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
};

const FleetTeamPage = ({ currentUser }) => {
    const { apiFetch } = useApi();
    
    // Estados de Listagem e Paginação
    const [teams, setTeams] = useState([]);
    const [loading, setLoading] = useState(false);
    const [hasSearched, setHasSearched] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);

    // Estado do Accordion (Sanfona)
    const [expandedTeamId, setExpandedTeamId] = useState(null);

    // Estados do Modal
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingTeam, setEditingTeam] = useState(null);
    const [teamName, setTeamName] = useState('');
    const [members, setMembers] = useState([{ name: '', cnh: '' }]);
    const [formError, setFormError] = useState('');
    const [formLoading, setFormLoading] = useState(false);
    
    // NOVO ESTADO: Lista de Usuários do Sistema
    const [systemUsers, setSystemUsers] = useState([]);

    // Permissões
    const canView = currentUser?.is_super_user || currentUser?.permissions?.fleet_team_view;
    const canCreate = currentUser?.is_super_user || currentUser?.permissions?.fleet_team_create;
    const canEdit = currentUser?.is_super_user || currentUser?.permissions?.fleet_team_edit;
    const canDelete = currentUser?.is_super_user || currentUser?.permissions?.fleet_team_delete;

    // Busca a lista de usuários em segundo plano para o AutoComplete (Apenas se tiver permissão)
    useEffect(() => {
        if (canCreate || canEdit) {
            apiFetch('/users')
                .then(data => {
                    if (Array.isArray(data)) setSystemUsers(data);
                })
                .catch(err => console.error("Aviso: Falha ao carregar usuários para o autocomplete:", err));
        }
    }, [apiFetch, canCreate, canEdit]);

    const fetchTeams = useCallback(async (page = 1, forceSearchAll = false) => {
        if (!canView) return;
        setLoading(true);
        setHasSearched(true);
        setExpandedTeamId(null); // Fecha qualquer sanfona aberta ao mudar de página
        
        try {
            const params = new URLSearchParams();
            if (searchQuery && !forceSearchAll) params.append('search', searchQuery);
            params.append('page', page);

            const response = await apiFetch(`/fleet/teams?${params.toString()}`);
            
            if (response && Array.isArray(response.data)) {
                setTeams(response.data);
                setTotalPages(response.totalPages || 1);
                setCurrentPage(response.currentPage || 1);
            } else {
                setTeams([]);
                setTotalPages(1);
                setCurrentPage(1);
            }
        } catch (error) {
            console.error("Erro ao buscar equipes:", error);
            alert("Falha ao carregar as equipes.");
        } finally {
            setLoading(false);
        }
    }, [apiFetch, canView, searchQuery]);

    const handleSearchClick = () => {
        fetchTeams(1);
    };

    const handleSearchAllClick = () => {
        setSearchQuery('');
        fetchTeams(1, true);
    };

    const toggleExpand = (teamId) => {
        setExpandedTeamId(prev => prev === teamId ? null : teamId);
    };

    // --- LÓGICA DO MODAL E FORMULÁRIO ---
    const openModal = (team = null) => {
        setFormError('');
        if (team) {
            setEditingTeam(team);
            setTeamName(team.name);
            if (team.members && team.members.length > 0) {
                setMembers(team.members.map(m => ({ name: m.name, cnh: m.cnh || '' })));
            } else {
                setMembers([{ name: '', cnh: '' }]);
            }
        } else {
            setEditingTeam(null);
            setTeamName('');
            setMembers([{ name: '', cnh: '' }]);
        }
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setEditingTeam(null);
    };

    const handleAddMemberRow = () => {
        setMembers([...members, { name: '', cnh: '' }]);
    };

    const handleRemoveMemberRow = (index) => {
        const newMembers = [...members];
        newMembers.splice(index, 1);
        setMembers(newMembers);
    };

    const handleMemberChange = (index, field, value) => {
        const newMembers = [...members];
        newMembers[index][field] = value;
        setMembers(newMembers);
    };

    const handleSave = async (e) => {
        e.preventDefault();
        setFormError('');
        setFormLoading(true);

        if (!teamName.trim()) {
            setFormError("O nome da equipe é obrigatório.");
            setFormLoading(false);
            return;
        }

        // Filtra membros vazios para não enviar pro banco dados em branco
        const validMembers = members.filter(m => m.name.trim() !== '');

        if (validMembers.length === 0) {
            setFormError("Adicione pelo menos um colaborador com nome à equipe.");
            setFormLoading(false);
            return;
        }

        const payload = {
            name: teamName.trim(),
            members: validMembers
        };

        try {
            if (editingTeam) {
                await apiFetch(`/fleet/teams/${editingTeam.id}`, {
                    method: 'PUT',
                    body: payload
                });
            } else {
                await apiFetch('/fleet/teams', {
                    method: 'POST',
                    body: payload
                });
            }
            closeModal();
            if (hasSearched) fetchTeams(currentPage);
        } catch (error) {
            setFormError(`Erro ao salvar: ${error.message}`);
        } finally {
            setFormLoading(false);
        }
    };

    const handleDelete = async (id, e) => {
        e.stopPropagation(); // Evita que o click abra/feche a sanfona
        if (window.confirm("Tem a certeza que deseja excluir esta equipe e todos os seus colaboradores? Esta ação não pode ser desfeita.")) {
            try {
                await apiFetch(`/fleet/teams/${id}`, { method: 'DELETE' });
                if (hasSearched) fetchTeams(currentPage);
            } catch (error) {
                alert(`Erro ao excluir: ${error.message}`);
            }
        }
    };

    if (!canView) {
        return <div className="p-8 text-center text-gray-500 font-bold">Você não tem permissão para acessar este módulo.</div>;
    }

    return (
        <div className="space-y-6 animate-in fade-in zoom-in-95 duration-300">
            {/* CABEÇALHO */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <div>
                    <h1 className="text-2xl font-black text-gray-800 flex items-center gap-2">
                        <Users className="text-blue-600" size={28} />
                        Cadastro de Equipes
                    </h1>
                    <p className="text-gray-500 text-sm mt-1">Gerencie os veículos/equipes e seus respectivos colaboradores.</p>
                </div>
                {canCreate && (
                    <button 
                        onClick={() => openModal()} 
                        className="bg-blue-600 text-white px-4 py-2.5 rounded-lg font-bold shadow-sm hover:bg-blue-700 transition-colors flex items-center gap-2 w-full md:w-auto justify-center"
                    >
                        <Plus size={18} /> Nova Equipe
                    </button>
                )}
            </div>

            {/* FILTROS E BUSCA */}
            <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-200">
                <div className="flex flex-col md:flex-row gap-4 items-end">
                    <div className="flex-1 relative w-full">
                        <label className="text-xs font-bold text-gray-500 uppercase block mb-1">Pesquisar Equipe ou Colaborador</label>
                        <div className="relative">
                            <Search className="absolute left-3 top-2.5 text-gray-400" size={16} />
                            <input 
                                type="text" 
                                placeholder="Nome da equipe, nome do colaborador ou CNH..." 
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                onKeyDown={(e) => e.key === 'Enter' && handleSearchClick()}
                                className="w-full pl-9 pr-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-1 focus:ring-blue-500 outline-none"
                            />
                        </div>
                    </div>
                    
                    <div className="flex gap-2 w-full md:w-auto">
                        <button 
                            onClick={handleSearchClick} 
                            className="flex-1 md:flex-none bg-gray-800 text-white px-5 py-2.5 rounded-lg font-bold shadow-sm hover:bg-gray-900 transition-colors flex items-center justify-center gap-2 h-[42px]"
                        >
                            <Search size={16} /> Buscar
                        </button>
                        <button 
                            onClick={handleSearchAllClick} 
                            className="flex-1 md:flex-none bg-white text-gray-700 border border-gray-300 px-5 py-2.5 rounded-lg font-bold shadow-sm hover:bg-gray-50 transition-colors flex items-center justify-center gap-2 h-[42px]"
                        >
                            <List size={16} /> Ver Todos
                        </button>
                    </div>
                </div>
            </div>

            {/* LISTA DE EQUIPES (ACCORDION) */}
            {hasSearched && (
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden animate-in slide-in-from-bottom-4">
                    {loading ? (
                        <div className="p-12 text-center text-gray-500 font-medium">Buscando equipes...</div>
                    ) : teams.length === 0 ? (
                        <div className="p-12 text-center text-gray-500 font-medium italic bg-gray-50/50">Nenhuma equipe encontrada. Tente buscar novamente.</div>
                    ) : (
                        <div className="divide-y divide-gray-200">
                            {teams.map(team => {
                                const isExpanded = expandedTeamId === team.id;
                                return (
                                    <div key={team.id} className="transition-colors hover:bg-gray-50/50">
                                        {/* HEADER DO ACCORDION */}
                                        <div 
                                            className="flex flex-col md:flex-row md:items-center justify-between p-4 cursor-pointer"
                                            onClick={() => toggleExpand(team.id)}
                                        >
                                            <div className="flex items-center gap-3 mb-3 md:mb-0">
                                                <div className="p-2 bg-blue-100 text-blue-700 rounded-lg">
                                                    <Users size={20} />
                                                </div>
                                                <div>
                                                    <h3 className="font-bold text-lg text-gray-800">{team.name}</h3>
                                                    <p className="text-xs text-gray-500 font-medium mt-0.5">
                                                        {team.members?.length || 0} {(team.members?.length === 1) ? 'Colaborador' : 'Colaboradores'}
                                                    </p>
                                                </div>
                                            </div>
                                            
                                            <div className="flex items-center justify-between md:justify-end gap-4 w-full md:w-auto">
                                                {(canEdit || canDelete) && (
                                                    <div className="flex gap-2">
                                                        {canEdit && (
                                                            <button 
                                                                onClick={(e) => { e.stopPropagation(); openModal(team); }} 
                                                                className="p-2 text-blue-600 bg-blue-50 hover:bg-blue-100 rounded transition-colors" 
                                                                title="Editar Equipe"
                                                            >
                                                                <Edit size={16} />
                                                            </button>
                                                        )}
                                                        {canDelete && (
                                                            <button 
                                                                onClick={(e) => handleDelete(team.id, e)} 
                                                                className="p-2 text-red-600 bg-red-50 hover:bg-red-100 rounded transition-colors" 
                                                                title="Excluir Equipe"
                                                            >
                                                                <Trash2 size={16} />
                                                            </button>
                                                        )}
                                                    </div>
                                                )}
                                                <div className="text-gray-400">
                                                    {isExpanded ? <ChevronUp size={24} /> : <ChevronDown size={24} />}
                                                </div>
                                            </div>
                                        </div>

                                        {/* CORPO DO ACCORDION (LISTA DE COLABORADORES) */}
                                        {isExpanded && (
                                            <div className="bg-gray-50 border-t border-gray-100 p-4 animate-in slide-in-from-top-2 duration-200">
                                                {(!team.members || team.members.length === 0) ? (
                                                    <p className="text-sm text-gray-500 italic">Esta equipe não possui colaboradores cadastrados.</p>
                                                ) : (
                                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                                                        {team.members.map(member => (
                                                            <div key={member.id} className="bg-white p-3 rounded-lg border border-gray-200 shadow-sm flex items-start gap-3">
                                                                <div className="p-1.5 bg-gray-100 rounded text-gray-500 mt-0.5">
                                                                    <User size={16} />
                                                                </div>
                                                                <div className="flex-1 overflow-hidden">
                                                                    <p className="font-bold text-gray-800 text-sm truncate" title={member.name}>{member.name}</p>
                                                                    {member.cnh ? (
                                                                        <div className="flex items-center gap-1.5 mt-1 text-xs text-gray-500 font-medium">
                                                                            <CreditCard size={12} className="text-blue-500"/> CNH: {member.cnh}
                                                                        </div>
                                                                    ) : (
                                                                        <div className="text-xs text-gray-400 italic mt-1">Sem CNH vinculada</div>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    {/* PAGINAÇÃO */}
                    {!loading && totalPages > 1 && (
                        <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
                            <div className="flex-1 flex justify-between sm:hidden">
                                <button onClick={() => fetchTeams(currentPage - 1)} disabled={currentPage === 1} className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50">
                                    Anterior
                                </button>
                                <span className="text-sm text-gray-700 py-2">Pág {currentPage} de {totalPages}</span>
                                <button onClick={() => fetchTeams(currentPage + 1)} disabled={currentPage === totalPages} className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50">
                                    Próximo
                                </button>
                            </div>
                            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                                <div>
                                    <p className="text-sm text-gray-700">
                                        Página <span className="font-medium">{currentPage}</span> de <span className="font-medium">{totalPages}</span>
                                    </p>
                                </div>
                                <div>
                                    <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                                        <button onClick={() => fetchTeams(currentPage - 1)} disabled={currentPage === 1} className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50">
                                            <span className="sr-only">Anterior</span>
                                            <ChevronLeft className="h-5 w-5" aria-hidden="true" />
                                        </button>
                                        
                                        {[...Array(totalPages)].map((_, i) => (
                                            <button
                                                key={i + 1}
                                                onClick={() => fetchTeams(i + 1)}
                                                className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${currentPage === i + 1 ? 'z-10 bg-blue-50 border-blue-500 text-blue-600' : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'}`}
                                            >
                                                {i + 1}
                                            </button>
                                        ))}

                                        <button onClick={() => fetchTeams(currentPage + 1)} disabled={currentPage === totalPages} className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50">
                                            <span className="sr-only">Próximo</span>
                                            <ChevronRight className="h-5 w-5" aria-hidden="true" />
                                        </button>
                                    </nav>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* MODAL DE CRIAÇÃO E EDIÇÃO */}
            {isModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-in fade-in duration-200">
                    <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col animate-in slide-in-from-bottom-4 duration-300">
                        
                        <div className="flex items-center justify-between p-5 border-b border-gray-200 bg-gray-50 shrink-0 rounded-t-xl">
                            <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                                <Users className="text-blue-600" />
                                {editingTeam ? 'Editar Equipe' : 'Nova Equipe'}
                            </h2>
                            <button onClick={closeModal} className="p-2 rounded-full hover:bg-gray-200 text-gray-500 transition-colors">
                                <X size={20} />
                            </button>
                        </div>
                        
                        <div className="p-6 overflow-y-auto flex-1">
                            {formError && (
                                <div className="p-3 mb-4 text-sm font-semibold text-red-700 bg-red-100 border border-red-200 rounded-lg">
                                    {formError}
                                </div>
                            )}

                            <div className="mb-6">
                                <label className="block text-sm font-bold text-gray-700 mb-1">Nome da Equipe ou Referência do Veículo</label>
                                <input 
                                    type="text" 
                                    value={teamName} 
                                    onChange={(e) => setTeamName(e.target.value)} 
                                    placeholder="Ex: Equipe Alfa - Kombi Branca"
                                    required 
                                    className="w-full p-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                                />
                            </div>

                            <div>
                                <div className="flex items-center justify-between mb-3 border-b border-gray-100 pb-2">
                                    <h3 className="font-bold text-gray-700">Colaboradores da Equipe</h3>
                                    <button 
                                        type="button" 
                                        onClick={handleAddMemberRow} 
                                        className="text-xs font-bold text-blue-600 hover:text-blue-800 flex items-center gap-1 bg-blue-50 px-2 py-1 rounded"
                                    >
                                        <Plus size={14}/> Adicionar Linha
                                    </button>
                                </div>

                                <div className="space-y-3">
                                    {members.map((member, index) => (
                                        <div key={index} className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-3 bg-gray-50 p-3 rounded-lg border border-gray-200">
                                            <div className="flex-1 w-full">
                                                <label className="text-[10px] uppercase font-bold text-gray-500 block mb-1">Nome do Colaborador *</label>
                                                {/* --- INPUT SUBSTITUÍDO PELO AUTOCOMPLETE --- */}
                                                <UserSearchInput 
                                                    value={member.name}
                                                    onChange={(val) => handleMemberChange(index, 'name', val)}
                                                    onSelect={(user) => {
                                                        handleMemberChange(index, 'name', user.full_name || user.username);
                                                        handleMemberChange(index, 'cnh', user.cnh || '');
                                                    }}
                                                    usersList={systemUsers}
                                                />
                                            </div>
                                            <div className="flex-1 w-full sm:max-w-[200px]">
                                                <label className="text-[10px] uppercase font-bold text-gray-500 block mb-1">CNH (Opcional)</label>
                                                <input 
                                                    type="text" 
                                                    placeholder="Nº da CNH..."
                                                    value={member.cnh}
                                                    onChange={(e) => handleMemberChange(index, 'cnh', e.target.value)}
                                                    className="w-full p-2 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 outline-none font-mono"
                                                />
                                            </div>
                                            <div className="mt-2 sm:mt-5">
                                                <button 
                                                    type="button"
                                                    onClick={() => handleRemoveMemberRow(index)}
                                                    disabled={members.length === 1} // Não deixa excluir se for o único
                                                    className="p-2 text-red-500 hover:bg-red-100 rounded transition-colors disabled:opacity-30 disabled:hover:bg-transparent"
                                                    title="Remover Colaborador"
                                                >
                                                    <Trash2 size={18} />
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>

                        <div className="flex justify-end gap-3 p-5 border-t border-gray-200 shrink-0 bg-gray-50 rounded-b-xl">
                            <button 
                                type="button" 
                                onClick={closeModal} 
                                className="px-5 py-2.5 font-bold text-gray-600 bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors"
                            >
                                Cancelar
                            </button>
                            <button 
                                onClick={handleSave}
                                disabled={formLoading} 
                                className="px-5 py-2.5 font-bold text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:bg-blue-400 transition-colors flex items-center gap-2"
                            >
                                {formLoading ? 'Salvando...' : 'Salvar Equipe'}
                            </button>
                        </div>

                    </div>
                </div>
            )}
        </div>
    );
};

export default FleetTeamPage;